import React, { useState } from 'react';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { authApiService } from '../services/apiServices';

const RegisterForm: React.FC = () => {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [code, setCode] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInitSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authApiService.initRegistration(email);
      if (response?.data?.token) {
        setToken(response.data.token);
        setStep('code');
      }
    } catch (err: any) {
      const body = err?.body as { code?: string; message?: string } | undefined;
      if (body?.code === 'user_already_exists') {
        setError('Пользователь с таким email уже зарегистрирован');
      } else if (body?.code === 'registration_rate_limited') {
        setError('Слишком много запросов. Попробуйте позже');
      } else {
        setError(body?.message || 'Ошибка при отправке кода подтверждения');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authApiService.completeRegistration(token, code, fullName);
      window.location.replace('/profile');
    } catch (err: any) {
      const body = err?.body as { code?: string; message?: string } | undefined;
      if (body?.code === 'invalid_verification_code') {
        setError('Неверный код подтверждения');
      } else if (body?.code === 'registration_token_not_found') {
        setError('Токен регистрации истёк. Начните регистрацию заново');
      } else if (body?.code === 'registration_rate_limited') {
        setError('Слишком много попыток. Попробуйте позже');
      } else {
        setError(body?.message || 'Ошибка при завершении регистрации');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow w-100" style={{ maxWidth: '400px', margin: '0 auto' }}>
      <Card.Header className="bg-primary text-white text-center py-3">
        <Card.Title className="mb-0">
          <h3 className="mb-0">Регистрация</h3>
        </Card.Title>
      </Card.Header>
      <Card.Body className="p-4">
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {step === 'email' ? (
          <Form onSubmit={handleInitSubmit}>
            <p className="text-muted mb-3">
              Укажите корпоративный email. На него будет отправлен код подтверждения.
            </p>

            <Form.Group className="mb-4" controlId="formEmail">
              <Form.Label className="fw-bold">Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Введите email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="py-2"
              />
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100 py-2 fw-semibold"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Отправка...
                </>
              ) : (
                'Отправить код'
              )}
            </Button>
          </Form>
        ) : (
          <Form onSubmit={handleCompleteSubmit}>
            <Alert variant="info" className="mb-3">
              Код подтверждения отправлен на <strong>{email}</strong>
            </Alert>

            <Form.Group className="mb-3" controlId="formCode">
              <Form.Label className="fw-bold">Код подтверждения</Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите 6-значный код"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                pattern="[0-9]{6}"
                required
                disabled={loading}
                className="py-2"
              />
            </Form.Group>

            <Form.Group className="mb-4" controlId="formFullName">
              <Form.Label className="fw-bold">ФИО</Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите ФИО"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={loading}
                className="py-2"
              />
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100 py-2 fw-semibold"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Регистрация...
                </>
              ) : (
                'Завершить регистрацию'
              )}
            </Button>

            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-link text-decoration-none"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setFullName('');
                  setError('');
                }}
              >
                Изменить email
              </button>
            </div>
          </Form>
        )}

        <div className="text-center mt-3">
          <Link to="/login" className="text-decoration-none">
            Уже есть аккаунт? Войти
          </Link>
        </div>
      </Card.Body>
    </Card>
  );
};

export default RegisterForm;
